import React, { FC, ReactNode, useState } from 'react';
import {
    Badge,
    Box,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    Popover,
    Typography
} from '@mui/material';

import useLiveRefresh from './useLiveRefresh';

interface Props {
    /** Tooltip / aria label, e.g. "Online users". */
    title: string;
    icon: ReactNode;
    count: number;
    /** Badge colour. */
    color: 'error' | 'success';
    /** Re-read the count. Called on mount, on the poll, on tab focus and on each `refreshOn` event. */
    refresh: () => void;
    /** Socket events that mean the count may have changed. */
    refreshOn: string[];
    /** Called each time the popover opens, so its list is fresh rather than as of page load. */
    onOpen?: () => void;
    loading?: boolean;
    /** Popover body. Gets `close` so a row can dismiss the popover as it navigates. */
    children: (close: () => void) => ReactNode;
    /** Links under the list — each closes the popover before navigating. */
    footer: Array<{ label: string; onClick: () => void }>;
}

/**
 * Header icon + live count badge + peek popover. The shared shell behind the online-users and
 * tickets indicators: the count stays visible on every page, and clicking shows who / what without
 * leaving the page — the footer link is for when you actually want to act.
 */
const HeaderCountButton: FC<Props> = ({
    title,
    icon,
    count,
    color,
    refresh,
    refreshOn,
    onOpen,
    loading,
    children,
    footer
}) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    useLiveRefresh(refresh, refreshOn);

    const open = (e: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(e.currentTarget);
        onOpen?.();
    };
    const close = () => setAnchorEl(null);

    return (
        <>
            <IconButton
                size="small"
                onClick={open}
                title={title}
                aria-label={title}
                sx={{ color: 'inherit' }}
            >
                <Badge
                    badgeContent={count}
                    color={color}
                    max={99}
                    sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16 } }}
                >
                    {icon}
                </Badge>
            </IconButton>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Box sx={{ width: 320 }}>
                    <Typography sx={{ px: 2, pt: 1.5, pb: 1, fontWeight: 600, fontSize: '0.9rem' }}>
                        {title} ({count})
                    </Typography>
                    <Divider />
                    <Box sx={{ maxHeight: '50vh', overflowY: 'auto' }}>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress size={22} />
                            </Box>
                        ) : (
                            children(close)
                        )}
                    </Box>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, py: 0.5 }}>
                        {footer.map((link) => (
                            <Button
                                key={link.label}
                                size="small"
                                onClick={() => {
                                    close();
                                    link.onClick();
                                }}
                            >
                                {link.label}
                            </Button>
                        ))}
                    </Box>
                </Box>
            </Popover>
        </>
    );
};

export default HeaderCountButton;
